import { PlanogramItem, UnrangedPlanogramItem } from "@CommonTypes/merchflow/pog/item";
import React from "react";
import { clickDuration } from "src/components/Planogram/store/constants";
import { MouseButton } from "src/types/mouseButtons";

import {
  DraggedItemsPayload,
  DraggableItem,
  DraggableItemOriginal,
  DraggableItemWithDrop,
} from "./types";
import { PlanogramSidepanelTab } from "src/components/PlanogramExtensions/store/types";

// Object to keep information abour original parent or previous element to insert it back into correct position before state changes.
let draggableOriginalHolder: HTMLElement | null = null;

// Object to keep original draggable dom, cursor and element positions.
let draggableOriginal: DraggableItemOriginal | null = null;

// Object to hold dummy placeholder element that is render in place where the item will be dropped.
let draggablePlaceholder: DraggableItem | null;

// Object to hold dom element that is dragging and also dropper function to call when dragging stops.
let draggable: DraggableItemWithDrop | null = null;

// Timeout to be able to stop dragging before it starts (to register as lick to edit item).
let timeoutToCreateDraggable: NodeJS.Timeout | null = null;

let dragging = false;

export const onDragStart = ({
  event,
  draggedItems,
  refItemContainer,
  refItem,
  item,
  setSidepanelTab,
}: {
  draggedItems: DraggedItemsPayload;
  event: React.MouseEvent<HTMLDivElement>;
  refItemContainer: React.RefObject<HTMLDivElement | null>;
  refItem: React.RefObject<HTMLDivElement | null>;
  item: PlanogramItem | UnrangedPlanogramItem;
  setSidepanelTab: (panelTabType: PlanogramSidepanelTab) => void;
}) => {
  if (
    event.button !== MouseButton.PRIMARY ||
    !refItemContainer.current ||
    !refItem.current ||
    Boolean(draggableOriginal) ||
    Boolean(draggablePlaceholder) ||
    Boolean(draggableOriginalHolder) ||
    dragging
  ) {
    return;
  }

  // Stop event propagation, so shelf is not moved, when moving an item.
  event.stopPropagation();
  event.preventDefault();

  // Positions need to be calculated from actual item (because dropper is taking extra space while rendering on shelf).
  const { left: positionXLeft, top: positionYTop } = refItem.current.getBoundingClientRect();

  const { clientX: cursorXLeft, clientY: cursorYTop } = event;

  // Create draggable with a delay to make it able to click items.
  timeoutToCreateDraggable = setTimeout(() => {
    if (refItemContainer.current) {
      draggableOriginal = {
        domContainer: refItemContainer.current,
        dom: refItemContainer.current,
        item,
        cursorXLeft,
        cursorYTop,
        draggedItems,
        positionXLeft,
        positionYTop,
        restoreOriginalOnStop: draggedItems.mode === "move",
      };

      createDraggable(draggableOriginal);
      createDragMoveEventListener();
      createDragStopEventListener();
      setSidepanelTab("UNRANGED_ITEMS");
    }

    timeoutToCreateDraggable = null;
  }, clickDuration);

  // Create drag stop to prevent dragging if needed.
  createDragStopEventListener();
};

export const onUnrangedItemDragStart = ({
  event,
  refUnrangedItem,
  refItemContainer,
  item,
}: {
  event: React.MouseEvent<HTMLDivElement>;
  refUnrangedItem: React.RefObject<HTMLDivElement | null>;
  refItemContainer: React.RefObject<HTMLDivElement | null>;
  item: UnrangedPlanogramItem;
}) => {
  if (
    event.button !== MouseButton.PRIMARY ||
    !refItemContainer.current ||
    !refUnrangedItem.current
  ) {
    return;
  }

  const { clientX: cursorXLeft, clientY: cursorYTop } = event;

  // Find dimensions from invisible item element.
  const { width, height } = refItemContainer.current.getBoundingClientRect();

  draggableOriginal = {
    domContainer: refUnrangedItem.current,
    dom: refItemContainer.current,
    item,
    cursorXLeft,
    cursorYTop,
    draggedItems: {
      anchorItemId: item.uniqueId,
      itemIds: [item.uniqueId],
      mode: "move",
    },
    // Start showing draggable item at center of cursor.
    positionXLeft: cursorXLeft - width / 2,
    positionYTop: cursorYTop - height / 2,
    restoreOriginalOnStop: true,
  };

  createDraggable(draggableOriginal);
  createDragMoveEventListener();
  createDragStopEventListener();
};

const createDraggable = (draggableOriginal: DraggableItemOriginal) => {
  dragging = true;

  // Save original holders to make sure we can insert original back into DOM later.
  draggableOriginalHolder = draggableOriginal.domContainer.previousSibling as HTMLElement;

  const { positionXLeft: draggableXLeft, positionYTop: draggableyTop } = draggableOriginal;

  // Create placeholder and render on screen in place of original item.
  const domPlaceholder = draggableOriginal.dom.cloneNode(true) as HTMLDivElement;
  draggablePlaceholder = {
    dom: domPlaceholder,
    item: draggableOriginal.item,
  };
  domPlaceholder.setAttribute("data-placeholder", "");
  if (draggableOriginal.restoreOriginalOnStop) {
    draggableOriginal.domContainer.after(domPlaceholder);
  }

  // Create draggable and render on screen in exact place of the original item.
  const domDraggable = draggableOriginal.dom.cloneNode(true) as HTMLDivElement;
  draggable = {
    dom: domDraggable,
    item: draggableOriginal.item,
  };
  domDraggable.setAttribute("data-draggable", "");
  domDraggable.setAttribute("style", `--xLeft: ${draggableXLeft}px; --yTop: ${draggableyTop}px`);
  document.body.appendChild(draggable.dom);

  // Remove original only for move drags so copy drags keep the source item visible.
  if (draggableOriginal.restoreOriginalOnStop) {
    draggableOriginal.domContainer.remove();
  }

  document.body.setAttribute("data-dnd", "");
};

// Create event listener on page to be able to drag item around.
const createDragMoveEventListener = () => {
  document.addEventListener("pointermove", onDragMove);
};

const onDragMove = (event: MouseEvent) => {
  if (!draggableOriginal || !draggable) {
    return;
  }

  const { clientX: cursorXLeft, clientY: cursorYTop } = event;

  const differenceCursorXLeft = draggableOriginal.cursorXLeft - cursorXLeft;
  const differenceCursorYTop = draggableOriginal.cursorYTop - cursorYTop;

  const positionXLeftNew = draggableOriginal.positionXLeft - differenceCursorXLeft;
  const positionYTopNew = draggableOriginal.positionYTop - differenceCursorYTop;

  draggable.dom.setAttribute(
    "style",
    `--xLeft: ${Math.round(positionXLeftNew)}px; --yTop: ${Math.round(positionYTopNew)}px`,
  );
};

// Create event listener on page that only fires once to finish item dragging.
const createDragStopEventListener = () => {
  document.addEventListener("pointerup", onDragStop, { once: true });
};

const onDragStop = () => {
  // Prevent next item dragging immediately.
  setTimeout(() => {
    dragging = false;
  }, 50);

  if (timeoutToCreateDraggable) {
    clearTimeout(timeoutToCreateDraggable);
  }

  // Cleanup page.
  document.removeEventListener("pointermove", onDragMove);
  document.body.removeAttribute("data-dnd");

  // Remove dragged and placeholder items, insert original item back into original location.
  // This is required for react to render items properly on next render cycle.
  draggable?.dom.remove();
  draggablePlaceholder?.dom.remove();

  if (draggableOriginal?.restoreOriginalOnStop && draggableOriginal.domContainer) {
    draggableOriginalHolder?.after(draggableOriginal.domContainer);
  }

  // Activate onDrop function that actually modifies the state to trigger re-render.
  if (draggable?.onDrop && draggableOriginal) {
    draggable.onDrop(draggableOriginal.draggedItems);
  }

  // Cleanup all dragging related objects.
  draggable = null;
  draggablePlaceholder = null;
  draggableOriginal = null;
  draggableOriginalHolder = null;
};

// Move placeholder around based on the item you are dragging over.
// Save dropper function to be called when dragging stops.
export const onDropOverToItem = ({
  refItemContainer,
  side,
  onDrop,
}: {
  refItemContainer: React.RefObject<HTMLDivElement | null>;
  side: "left" | "right";
  onDrop: (payload: DraggedItemsPayload) => void;
}) => {
  if (!draggable || !draggableOriginal || !draggablePlaceholder || !refItemContainer.current) {
    return;
  }

  draggable.onDrop = onDrop;

  if (side === "left") {
    refItemContainer.current.before(draggablePlaceholder.dom);
  } else if (side === "right") {
    refItemContainer.current.after(draggablePlaceholder.dom);
  }
};

// Move placeholder around based on the shelf you are dragging over.
// Save dropper function to be called when dragging stops.
export const onDropOverToShelf = ({
  refItems,
  onDrop,
}: {
  refItems: React.RefObject<HTMLDivElement | null>;
  onDrop: (payload: DraggedItemsPayload) => void;
}) => {
  if (!draggable || !draggableOriginal || !draggablePlaceholder || !refItems.current) {
    return;
  }

  draggable.onDrop = onDrop;
  refItems.current.append(draggablePlaceholder.dom);
};

// Read-only view of the drag in flight, for the Konva canvas host: while a
// draggable exists the stage renders its own insertion ghost (the canvas has
// no DOM placeholder to move), and it needs the dragged item ids to size it.
export const getActiveDragPayload = (): DraggedItemsPayload | null =>
  draggable && draggableOriginal ? draggableOriginal.draggedItems : null;

// Save dropper function to be called when dragging stops over the Konva
// canvas. The canvas has no per-item DOM droppers — the stage host registers
// one handler while the pointer is over it and resolves the drop target from
// the pointer position itself, so no placeholder is moved here.
export const onDropOverToCanvas = ({
  onDrop,
}: {
  onDrop: (payload: DraggedItemsPayload) => void;
}) => {
  if (!draggable || !draggableOriginal) {
    return;
  }

  draggable.onDrop = onDrop;
};

// Move placeholder around over to infront of other unranged items.
// Save dropper function to be called when dragging stops.
export const onDropOverToUnrangedItems = ({
  refUnrangedItems,
  onDrop,
}: {
  refUnrangedItems: React.RefObject<HTMLDivElement | null>;
  onDrop: (payload: DraggedItemsPayload) => void;
}) => {
  if (!draggable || !draggableOriginal || !draggablePlaceholder || !refUnrangedItems.current) {
    return;
  }

  draggable.onDrop = onDrop;

  refUnrangedItems.current.insertBefore(
    draggablePlaceholder.dom,
    refUnrangedItems.current.firstElementChild,
  );
};
